import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography, Tabs, Tab, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import EditOrderForm from './EditOrderForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';

export default function OrderList() {
  const { state } = useLocation();
  const facilityId = state?.facilityId;
  const facilityName = state?.facilityName;
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [openEditOrderForm, setOpenEditOrderForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('processed');
  const [filteredOrders, setFilteredOrders] = useState([]);

  useEffect(() => {
    setFilteredOrders(orders.filter(order => activeTab === 'processed' ? order.approved : !order.approved));
}, [activeTab, orders]);

  useEffect(() => {
    axiosInstance.get(facilityId ? `/order/orders?id=${facilityId}` : "/order/orders")
      .then(res => {
        setOrders(res.data);
        setFilteredOrders(res.data.filter(order => activeTab === 'processed' ? order.approved : !order.approved));
      })
        .catch(() => enqueueSnackbar('Orders list failed', { variant: 'error' }))
        .finally(() => setLoading(false));
  }, [facilityId]);

  const deleteOrder = (params, id) => {
    axiosInstance.post(`/order/deleteOrder?id=${id}`)
      .then(() => {
        setOrders(orders => [...orders.filter(order => order.id !== id)]);
        params.api.applyTransaction({ remove: [params.data] });
        enqueueSnackbar('Order deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Order delete failed', { variant: 'error' }))
  };

  const columns = [
    {
      field: 'id',
      headerName: 'PO#',
      width: 100
    },
    {
      field: 'importedOrderNumber',
      headerName: 'Customer PO#',
      width: 150
    },
    {
      field: 'facility',
      minWidth: 150,
      flex: 1,    
    },
    {
      field: 'parent',
      minWidth: 150,
    },
    {
      field: 'orderersEmail',
      minWidth: 150,
    },
    {
      field: 'itemCount',
      headerName: 'Items',
      width: 80,   
    },
    {
      field: 'orderTotal',
      width: 130,
      cellRenderer: (params) => params.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    },
    {
      field: 'approved',
      headerName: 'Sent',
      cellRenderer: (params) => params.value ? <CheckCircleIcon color="primary" sx={{ mt: 1 }} /> : "",
      width: 80,
    },
    {
      field: 'dateAdded',
      cellRenderer: (params) => new Date(params.value).toDateString(),
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <EditIconButton onClick={() => { setEditOrder(params.data); setOpenEditOrderForm(true); }} />
        <DeleteIconButton content={`Are you sure you want to delete order ${params.data.id}?`} action={() => deleteOrder(params, params.data.id)} />
      </Stack>
    }
  ];

  return (<>
    <Helmet>
      <title>Order List | NHIDirect</title>
    </Helmet>
    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        {facilityId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: 'black', "&:hover": { backgroundColor: "transparent", } }} >
              <ArrowBackIcon />
            </IconButton>
            <Typography fontWeight={600}>{facilityName}</Typography>
          </Box>)}
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => navigate("/admin/orderSheet")} color="primary" size="small">
          <Typography>New Order</Typography>
        </Button>
      </AGGridQuickFilterWrapper>
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} TabIndicatorProps={{ style: { display: 'none' } }}>
        <Tab label="Processed Orders" value="processed" sx={{ textDecoration: activeTab === 'processed' ? 'underline' : 'none' }} />
        <Tab label="Pending Orders" value="pending" sx={{ textDecoration: activeTab === 'pending' ? 'underline' : 'none' }} />
      </Tabs>
      {!loading ?
        <Box sx={{ height: "max(500px, 100%)" }}>
          <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={filteredOrders} getRowStyle={params => {
              if (params.data.invoiced) return { backgroundColor: '#e6f0ff' }; 
            if (params.data.shipped)  return { backgroundColor: '#e8f8e8' };
              return {}}}/>
        </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <EditOrderForm editOrder={editOrder} open={openEditOrderForm} close={() => setOpenEditOrderForm(false)} setOrders={setOrders} />
    </Box>
  </>);
}
